import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import { updateCustomerAddress, fetchCustomerById } from '../../features/customer/customer.slice';
import { COUNTRIES, RESTRICTED_COUNTRY_CODES } from '../../utils/countries';
import './edit-billing-address-modal.scss';

interface BillingAddressData {
  country: string;
  stateCounty: string;
  city: string;
  addressLine: string;
  zipCode: string;
}

interface EditBillingAddressModalProps {
  show: boolean;
  onHide: () => void;
  onSave: (addressData: BillingAddressData) => void;
  initialData?: BillingAddressData;
  customerId?: number; // Add customerId prop
}

const EditBillingAddressModal: React.FC<EditBillingAddressModalProps> = ({
  show,
  onHide,
  onSave,
  initialData,
  customerId
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const updateAddressLoading = useAppSelector((state: any) => state?.customer?.updateAddressLoading || false);
  const fetchCustomerByIdLoading = useAppSelector((state: any) => state?.customer?.fetchCustomerByIdLoading || false);
  const selectedCustomerDetails = useAppSelector((state: any) => state?.customer?.selectedCustomerDetails);
  
  const [formData, setFormData] = useState<BillingAddressData>({
    country: initialData?.country || '',
    stateCounty: initialData?.stateCounty || '',
    city: initialData?.city || '',
    addressLine: initialData?.addressLine || '',
    zipCode: initialData?.zipCode || ''
  });

  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const [countrySearchQuery, setCountrySearchQuery] = useState('');
  const hasInitializedRef = useRef(false);

  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const stateDropdownRef = useRef<HTMLDivElement>(null);
  const countrySearchInputRef = useRef<HTMLInputElement>(null);

  // Memoize sorted countries to prevent re-sorting on every render
  const sortedCountries = useMemo(() => {
    return COUNTRIES.filter(country => country.code !== 'SE' && !RESTRICTED_COUNTRY_CODES.includes(country.code))
      .slice() // Create a copy before sorting
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const swedenCountry = useMemo(() => {
    return COUNTRIES.find(country => country.code === 'SE');
  }, []);

  const selectedCountry = useMemo(() => {
    return COUNTRIES.find(c => c.code === formData.country);
  }, [formData.country]);

  // Normalize for case- and accent-insensitive matching
  // (e.g. "sWedEn", "Sweden", "sverige", "Åland" all behave predictably).
  const normalizeForSearch = (str: string) =>
    (str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(new RegExp('[\\u0300-\\u036f]', 'g'), '') // strip accent marks
      .trim();

  // A country matches when the query is the start of the whole name OR the start
  // of any word in it. This prevents false hits like "Ryssland" matching
  // "Vitryssland" (mid-word substring), while still allowing partial searches
  // like "swe" -> Sweden or "arab" -> United Arab Emirates.
  const matchesQuery = (country: { name?: string; translationKey?: string }, query: string) => {
    return [country.name, t(country.translationKey || '')].some((name) => {
      const normalized = normalizeForSearch(name || '');
      if (normalized.startsWith(query)) return true;
      return normalized.split(/[\s(),.\/-]+/).some((word) => word.startsWith(query));
    });
  };

  // Filter countries based on search query.
  // Names that START with the query are ranked first for better relevance.
  const filteredCountries = useMemo(() => {
    const query = normalizeForSearch(countrySearchQuery);
    if (!query) {
      return sortedCountries;
    }
    const matches = sortedCountries.filter(country => matchesQuery(country, query));
    return matches.sort((a, b) => {
      const aStarts =
        normalizeForSearch(a.name).startsWith(query) ||
        normalizeForSearch(t(a.translationKey)).startsWith(query);
      const bStarts =
        normalizeForSearch(b.name).startsWith(query) ||
        normalizeForSearch(t(b.translationKey)).startsWith(query);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      return (t(a.translationKey) || a.name).localeCompare(t(b.translationKey) || b.name);
    });
  }, [sortedCountries, countrySearchQuery, t]);

  // Fetch customer data when modal opens
  useEffect(() => {
    if (show && customerId) {
      setHasUserEdited(false); // Reset edit flag when modal opens
      hasInitializedRef.current = false; // Reset initialization flag
      dispatch(fetchCustomerById(customerId));
    }
  }, [show, customerId, dispatch]);

  // Update form data when customer details are fetched (only if user hasn't edited)
  useEffect(() => {
    // Skip if already initialized or user has edited
    if (hasInitializedRef.current || hasUserEdited) {
      return;
    }

    if (selectedCustomerDetails) {
      // Handle both country code and country name
      let countryCode = selectedCustomerDetails.country || selectedCustomerDetails.countryCode || '';
      
      // If country is a full name (like "Sweden"), find the code
      if (countryCode && countryCode.length > 2) {
        const foundCountry = COUNTRIES.find(c => 
          c.name.toLowerCase() === countryCode.toLowerCase()
        );
        if (foundCountry) {
          countryCode = foundCountry.code;
        }
      }
      
      setFormData({
        country: countryCode,
        stateCounty: '',
        city: selectedCustomerDetails.city || '',
        addressLine: selectedCustomerDetails.address || '',
        zipCode: selectedCustomerDetails.zipCode || ''
      });
      hasInitializedRef.current = true;
    } else if (initialData) {
      setFormData({
        country: initialData.country || '',
        stateCounty: initialData.stateCounty || '',
        city: initialData.city || '',
        addressLine: initialData.addressLine || '',
        zipCode: initialData.zipCode || ''
      });
      // Only mark as initialized when no API fetch is pending (no customerId)
      // If customerId is present, keep hasInitializedRef false so the API response can override
      if (!customerId) {
        hasInitializedRef.current = true;
      }
    }
  }, [selectedCustomerDetails, hasUserEdited, customerId]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setIsCountryDropdownOpen(false);
        setCountrySearchQuery(''); // Clear search when closing
      }
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target as Node)) {
        setIsStateDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isCountryDropdownOpen && countrySearchInputRef.current) {
      setTimeout(() => {
        countrySearchInputRef.current?.focus();
      }, 100);
    } else if (!isCountryDropdownOpen) {
      setCountrySearchQuery(''); // Clear search when closing
    }
  }, [isCountryDropdownOpen]);

  const handleInputChange = (field: string, value: string) => {
    setHasUserEdited(true); // Mark that user has edited the form
    
    // Validate city field - only allow Latin letters and spaces
    if (field === 'city') {
      // Allow only Latin letters (A-Z, a-z), spaces, and hyphens
      const latinOnlyRegex = /^[A-Za-z\s-]*$/;
      if (!latinOnlyRegex.test(value)) {
        return; // Reject input if it contains non-Latin characters, numbers, or symbols
      }
    }
    
    // Validate zipCode field - letters, numbers, spaces and hyphens, max 10 characters
    if (field === 'zipCode') {
      const zipCodeRegex = /^[A-Za-z0-9\s,-]*$/;
      if (!zipCodeRegex.test(value)) {
        return; // Reject input if it contains disallowed characters
      }
      if (value.length > 10) {
        return; // Reject input if longer than 10 characters
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!customerId) {
      console.error('No customer ID provided');
      return;
    }

    try {
      // Convert empty strings to null
      // Convert country code to full country name for API
      const countryName = COUNTRIES.find(c => c.code === formData.country)?.name || formData.country;
      const addressData = {
        customerId: customerId,
        zipCode: formData.zipCode.trim() || null,
        address: formData.addressLine.trim() || null,
        city: formData.city.trim() || null,
        country: countryName.trim() || null
      };

      // Dispatch the Redux action
      const result = await dispatch(updateCustomerAddress(addressData)).unwrap();
      
      // Call the onSave callback with the form data
      onSave(formData);
      onHide();
    } catch (error) {
      console.error('Failed to update address:', error);
      // Error is already handled by toast in the thunk
    }
  };

  const handleCancel = () => {
    // Reset form data and edit flag when canceling
    setHasUserEdited(false);
    setFormData({
      country: initialData?.country || '',
      stateCounty: initialData?.stateCounty || '',
      city: initialData?.city || '',
      addressLine: initialData?.addressLine || '',
      zipCode: initialData?.zipCode || ''
    });
    onHide();
  };

  return (
    <Modal 
      show={show} 
      onHide={handleCancel}
      className="edit-billing-address-modal"
      centered
    >
      <Modal.Header className="custom-modal-header">
        <Modal.Title>{t('ADD_EDIT_BILLING_ADDRESS')}</Modal.Title>
        <Button 
          variant="link" 
          className="close-btn"
          onClick={handleCancel}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </Button>
      </Modal.Header>

      <Modal.Body className="custom-modal-body">
        {fetchCustomerByIdLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
            <Spinner animation="border" role="status">
              <span className="visually-hidden"></span>
            </Spinner>
          </div>
        ) : (
          <div className="form-fields">
          <Form.Group className="input-field">
            <Form.Label className="input-label">{t('COUNTRY')}</Form.Label>
            <div className="custom-dropdown" ref={countryDropdownRef}>
              <div
                className={`dropdown-trigger ${isCountryDropdownOpen ? 'open' : ''}`}
                onClick={() => {
                  if (!isCountryDropdownOpen) {
                    setIsCountryDropdownOpen(true);
                  }
                  // Focus synchronously, inside the tap's own event handler — iOS Safari
                  // only shows the on-screen keyboard for a focus() called directly within
                  // a user gesture, not one deferred to a later effect/timeout.
                  countrySearchInputRef.current?.focus();
                }}
              >
                <input
                  ref={countrySearchInputRef}
                  type="text"
                  className="dropdown-input"
                  autoComplete="off"
                  placeholder={t('ENTER_HERE')}
                  value={isCountryDropdownOpen
                    ? countrySearchQuery
                    : (formData.country && selectedCountry
                      ? (t(selectedCountry.translationKey) || selectedCountry.name)
                      : '')
                  }
                  onChange={(e) => {
                    setCountrySearchQuery(e.target.value);
                    if (!isCountryDropdownOpen) {
                      setIsCountryDropdownOpen(true);
                    }
                  }}
                  onFocus={() => {
                    if (!isCountryDropdownOpen) {
                      setIsCountryDropdownOpen(true);
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
                <svg
                  className={`dropdown-arrow ${isCountryDropdownOpen ? 'rotated' : ''}`}
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  onClick={(e) => {
                    e.stopPropagation();
                    const opening = !isCountryDropdownOpen;
                    setIsCountryDropdownOpen(opening);
                    if (opening) {
                      countrySearchInputRef.current?.focus();
                    }
                  }}
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="currentColor"
                    strokeWidth="1.67"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              {isCountryDropdownOpen && (
                <div className="dropdown-list">
                  <div className="dropdown-content">
                    {/* Sweden first - only show if matches search or no search */}
                    {swedenCountry && (!normalizeForSearch(countrySearchQuery) ||
                      matchesQuery(swedenCountry, normalizeForSearch(countrySearchQuery))
                    ) && (
                      <div
                        key={swedenCountry.code}
                        className={`list-item ${formData.country === swedenCountry.code ? 'selected' : ''}`}
                        onClick={() => {
                          handleInputChange('country', swedenCountry.code);
                          setIsCountryDropdownOpen(false);
                          setCountrySearchQuery('');
                        }}
                      >
                        <div className="text-input">
                          <div className="paragraph-container">
                            <p className="option-text">{t(swedenCountry.translationKey) || swedenCountry.name}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* All other countries sorted alphabetically and filtered */}
                    {filteredCountries.length > 0 ? (
                      filteredCountries.map((country) => (
                        <div
                          key={country.code}
                          className={`list-item ${formData.country === country.code ? 'selected' : ''}`}
                          onClick={() => {
                            handleInputChange('country', country.code);
                            setIsCountryDropdownOpen(false);
                            setCountrySearchQuery('');
                          }}
                        >
                          <div className="text-input">
                            <div className="paragraph-container">
                              <p className="option-text">{t(country.translationKey) || country.name}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="list-item no-results">
                        <div className="text-input">
                          <div className="paragraph-container">
                            <p className="option-text">{t('NO_RESULTS_FOUND') || 'No countries found'}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Form.Group>

          {/* <Form.Group className="input-field">
            <Form.Label className="input-label">{t('STATE_COUNTY')}</Form.Label>
            <div className="custom-dropdown" ref={stateDropdownRef}>
              <div
                className={`dropdown-trigger ${isStateDropdownOpen ? 'open' : ''}`}
                onClick={() => setIsStateDropdownOpen(!isStateDropdownOpen)}
              >
                <span className="dropdown-value">
                  {formData.stateCounty ? (
                    formData.stateCounty === 'stockholm' ? t('STOCKHOLM') :
                    formData.stateCounty === 'gothenburg' ? t('GOTHENBURG') :
                    formData.stateCounty === 'malmo' ? t('MALMO') :
                    formData.stateCounty === 'uppsala' ? t('UPPSALA') :
                    formData.stateCounty
                  ) : t('ENTER_HERE')}
                </span>
                <svg
                  className={`dropdown-arrow ${isStateDropdownOpen ? 'rotated' : ''}`}
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="currentColor"
                    strokeWidth="1.67"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              {isStateDropdownOpen && (
                <div className="dropdown-list">
                  <div className="dropdown-content">
                    {[
                      { value: 'stockholm', label: t('STOCKHOLM') },
                      { value: 'gothenburg', label: t('GOTHENBURG') },
                      { value: 'malmo', label: t('MALMO') },
                      { value: 'uppsala', label: t('UPPSALA') }
                    ].map((option) => (
                      <div
                        key={option.value}
                        className={`list-item ${formData.stateCounty === option.value ? 'selected' : ''}`}
                        onClick={() => {
                          handleInputChange('stateCounty', option.value);
                          setIsStateDropdownOpen(false);
                        }}
                      >
                        <div className="text-input">
                          <div className="paragraph-container">
                            <p className="option-text">{option.label}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Form.Group> */}

          <Form.Group className="input-field">
            <Form.Label>{t('CITY')}</Form.Label>
            <Form.Control
              type="text"
              placeholder={t('ENTER_HERE')}
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
            />
          </Form.Group>

          <Form.Group className="input-field">
            <Form.Label>{t('ADDRESS_LINE')}</Form.Label>
            <Form.Control
              type="text"
              placeholder={t('ENTER_HERE')}
              value={formData.addressLine}
              onChange={(e) => handleInputChange('addressLine', e.target.value)}
            />
          </Form.Group>

          <Form.Group className="input-field">
            <Form.Label>{t('ZIP_CODE')}</Form.Label>
            <Form.Control
              type="text"
              placeholder={t('ENTER_HERE')}
              value={formData.zipCode}
              maxLength={10}
              onChange={(e) => handleInputChange('zipCode', e.target.value)}
            />
          </Form.Group>
        </div>
        )}
      </Modal.Body>

      <Modal.Footer className="custom-modal-footer d-flex">
        <Button 
          variant="outline-primary" 
          className="cancel-btn"
          onClick={handleCancel}
          disabled={updateAddressLoading || fetchCustomerByIdLoading}
        >
          {t('CANCEL')}
        </Button>
        <Button 
          variant="primary" 
          className="submit-btn"
          onClick={handleSubmit}
          disabled={updateAddressLoading || fetchCustomerByIdLoading}
        >
          {updateAddressLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Submitting...
            </>
          ) : (
            t('SUBMIT')
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditBillingAddressModal;